import {
  snakeCaseKeys
} from "./chunk-KA7SO5SJ.js";

// src/hooks/useMutationStatus/useMutationStatus.tsx
import { useIsMutating } from "@tanstack/react-query";

// src/utils/globalMutex.ts
var GlobalMutex = class {
  constructor() {
    this.isLocked = false;
    this.queue = [];
    this.debounceWindowMs = 100;
  }
  // 100ms debounce window
  /**
   * Creates a signature for the request to identify duplicates.
   */
  createRequestSignature(fn) {
    const fnString = fn.toString();
    let hash = 0;
    for (let i = 0; i < fnString.length; i++) {
      const char = fnString.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString();
  }
  /**
   * Removes expired requests from the queue (older than debounce window).
   */
  cleanExpiredRequests() {
    const now = Date.now();
    this.queue = this.queue.filter(
      (request) => now - request.timestamp < this.debounceWindowMs
    );
  }
  /**
   * Finds and removes duplicate requests, resolving them with the same result.
   */
  deduplicateRequests(signature, result) {
    const duplicates = this.queue.filter(
      (request) => request.signature === signature
    );
    this.queue = this.queue.filter((request) => request.signature !== signature);
    duplicates.forEach((duplicate) => {
      duplicate.resolve(result);
    });
  }
  /**
   * Finds and rejects duplicate requests with the same error.
   */
  rejectDuplicates(signature, error) {
    const duplicates = this.queue.filter(
      (request) => request.signature === signature
    );
    this.queue = this.queue.filter((request) => request.signature !== signature);
    duplicates.forEach((duplicate) => {
      duplicate.reject(error);
    });
  }
  /**
   * Executes the provided function with exclusive access and deduplication.
   * If the mutex is already locked, the function will be queued.
   * Duplicate requests within the debounce window will be deduplicated.
   */
  async execute(fn) {
    if (false) {
      return fn();
    }
    const signature = this.createRequestSignature(fn);
    const timestamp = Date.now();
    this.cleanExpiredRequests();
    const existingRequest = this.queue.find(
      (request) => request.signature === signature
    );
    if (existingRequest) {
      return new Promise((resolve, reject) => {
        this.queue.push({
          fn,
          resolve,
          reject,
          signature,
          timestamp
        });
      });
    }
    return new Promise((resolve, reject) => {
      const executeFunction = async () => {
        try {
          this.isLocked = true;
          const result = await fn();
          resolve(result);
          this.deduplicateRequests(signature, result);
        } catch (error) {
          const errorObj = error instanceof Error ? error : new Error(String(error));
          reject(errorObj);
          this.rejectDuplicates(signature, errorObj);
        } finally {
          this.isLocked = false;
          this.processQueue();
        }
      };
      if (this.isLocked) {
        this.queue.push({
          fn,
          resolve,
          reject,
          signature,
          timestamp
        });
      } else {
        void executeFunction();
      }
    });
  }
  /**
   * Processes the next function in the queue if the mutex is not locked.
   */
  processQueue() {
    if (this.queue.length > 0 && !this.isLocked) {
      const nextRequest = this.queue.shift();
      if (nextRequest) {
        const { fn, resolve, reject, signature } = nextRequest;
        const executeFunction = async () => {
          try {
            this.isLocked = true;
            const result = await fn();
            resolve(result);
            this.deduplicateRequests(signature, result);
          } catch (error) {
            const errorObj = error instanceof Error ? error : new Error(String(error));
            reject(errorObj);
            this.rejectDuplicates(signature, errorObj);
          } finally {
            this.isLocked = false;
            this.processQueue();
          }
        };
        void executeFunction();
      }
    }
  }
  /**
   * Returns whether a request is currently being processed.
   */
  get isPending() {
    return this.isLocked;
  }
  /**
   * Returns the number of queued requests.
   */
  get queueLength() {
    return this.queue.length;
  }
  /**
   * Resets the mutex state (useful for testing).
   */
  reset() {
    this.isLocked = false;
    this.queue = [];
  }
};
var globalMutex = new GlobalMutex();

// src/hooks/useMutationStatus/useMutationStatus.tsx
import { useEffect, useState } from "react";
function useMutationStatus(mutationKey) {
  return useIsMutating({ mutationKey: [mutationKey] }) > 0;
}

// src/utils/apiRequest.tsx
var OPTIONS = {
  method: "POST",
  headers: {
    Accept: "application/json; charset=utf-8",
    "Content-Type": "application/json; charset=utf-8"
  }
};
var apiRequest = async (url, payload) => {
  return globalMutex.execute(async () => {
    const snakeCaseBody = snakeCaseKeys(payload);
    const body = JSON.stringify(snakeCaseBody);
    const headers = {
      ...OPTIONS.headers,
      "X-CSRF-Token": payload.authenticityToken ?? "not-provided"
    };
    const response = await fetch(url, { ...OPTIONS, headers, body });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  });
};

export {
  apiRequest,
  useMutationStatus
};
